/**
 * Matters waiting on a booking, on the clinic page (docs/ux/clinic.md
 * "Follow-ups", OPEN 80, provisional): every matter at 4 Paperwork Sorted
 * that needs an appointment, oldest due first, with the hub's booking form
 * opened in place. The matter is read when the form opens (so the form
 * previews the move the engine will allow) and again when it submits.
 */

import type { Status } from '@beam/core/status';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { api } from '@/app/api';
import type { MatterDetail, MatterRow, TransitionOption } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { DueCell } from '@/features/pipeline/columns';
import { BookingForm } from '@/features/matter/BookingForm';
import { detailKey } from '@/features/matter/useMatter';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAwaitingBooking, useClinicMove } from './useClinic';

const hubHref = (ref: string) => `/matters/${encodeURIComponent(ref)}`;

type Target = { ref: string; from: Status; option: TransitionOption | null };

export function AwaitingBooking({ doctor, offline }: { doctor: string | null; offline: boolean }) {
  const phone = useIsPhone();
  const client = useQueryClient();
  const awaiting = useAwaitingBooking();
  const [target, setTarget] = useState<Target | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const move = useClinicMove({ onConflict: () => setConflict(true), onMoved: () => setTarget(null) });
  const rows = (awaiting.data ?? []).filter((r) => !doctor || r.doctor.slug === doctor);

  async function openFor(ref: string) {
    setOpening(ref);
    try {
      const detail = await client.fetchQuery({ queryKey: detailKey(ref), queryFn: () => api<MatterDetail>(`/api/matters/${encodeURIComponent(ref)}`), staleTime: 0 });
      setConflict(false);
      setTarget({ ref, from: detail.matter.status, option: detail.options.find((o) => o.kind === 'appointment_booked') ?? null });
    } catch {
      toast.error(`Could not open ${ref}. Nothing changed.`);
    } finally {
      setOpening(null);
    }
  }

  const count = awaiting.data ? rows.length : null;
  return (
    <section aria-label="Awaiting booking" data-slot="awaiting-booking" className="rounded-xl border border-beam-line bg-beam-paper p-4">
      <header className="mb-2 flex items-baseline justify-between gap-3">
        <h2 className="eyebrow text-beam-text-muted">Awaiting booking{count !== null ? ` (${count})` : ''}</h2>
        <span className="text-xs text-beam-text-muted">Cases at 4 that need an appointment{doctor ? ', for the chosen doctor' : ''}</span>
      </header>
      {awaiting.isPending ? <Skeleton className="h-10 rounded-lg" /> : awaiting.isError ? (
        <p className="flex items-center gap-3 text-sm">The cases awaiting booking could not be loaded.<Button variant="link" className="h-auto p-0" onClick={() => void awaiting.refetch()}>Retry</Button></p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-beam-text-muted" data-slot="awaiting-empty">No cases awaiting booking.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-beam-line">
          {rows.map((m: MatterRow) => (
            <li key={m.ref} data-ref={m.ref} className={phone ? 'flex flex-col gap-2 py-3' : 'flex items-center gap-4 py-2'}>
              <span className={phone ? 'flex flex-col leading-tight' : 'flex min-w-0 flex-1 items-baseline gap-4'}>
                <Link to={hubHref(m.ref)} className={`tabular font-semibold hover:underline ${phone ? 'inline-flex min-h-11 items-center' : ''}`}>{m.ref}</Link>
                <span className="truncate text-sm" title={m.patientAsOnLoi}>{m.patientAsOnLoi}</span>
                <span className="truncate text-[13px] text-beam-text-muted">{m.doctor.name}</span>
                <span className="text-[13px] text-beam-text-muted tabular">{m.daysWaiting} {m.daysWaiting === 1 ? 'day' : 'days'} at 4</span>
                <span className="text-[13px]"><DueCell dueDate={m.dueDate} /></span>
              </span>
              <Button data-slot="book" variant="outline" size={phone ? 'lg' : 'sm'} className={phone ? 'h-11 w-full' : undefined} disabled={offline || opening !== null} aria-busy={opening === m.ref} onClick={() => void openFor(m.ref)}>Book</Button>
            </li>
          ))}
        </ul>
      )}
      {target ? (
        <BookingForm
          matterRef={target.ref}
          from={target.from}
          option={target.option}
          open
          onOpenChange={(open) => { if (!open) setTarget(null); }}
          busy={move.isPending}
          conflict={conflict}
          offline={offline}
          onSubmit={(values) => move.mutate({ ref: target.ref, event: 'appointment_booked', appointment: values })}
        />
      ) : null}
    </section>
  );
}
